import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';
import {
  TEMPLATE_CATEGORY_LABELS,
  type TemplateCategory,
} from '../../pages/settings/template-library/types';
import { getTemplateCategory } from '../../pages/settings/template-library/utils/templateTags';

/** Categories eligible for chart “Add Other Document” (non-appointment clinical documents). */
export const CHART_DOCUMENT_TEMPLATE_CATEGORIES: readonly TemplateCategory[] = [
  'treatment-planning',
  'administrative',
  'forms',
];

/** Category shown in the Add Other Document picker. */
export const ADD_OTHER_DOCUMENT_TEMPLATE_CATEGORY: TemplateCategory = 'treatment-planning';

/**
 * Questionnaire item audience — when absent, {@link inferQuestionnaireItemAudience} applies heuristics.
 * TODO: seed/document templates should set this extension explicitly on provider vs client fields.
 */
export const QUESTIONNAIRE_ITEM_AUDIENCE_EXT = 'https://unify.health/fhir/StructureDefinition/questionnaire-item-audience';

const ITEM_CONTROL_URL = 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';

function getItemControlCode(item: QuestionnaireItem): string | undefined {
  return item.extension?.find((e) => e.url === ITEM_CONTROL_URL)?.valueCodeableConcept?.coding?.[0]?.code;
}

/** True when the template is tagged for appointment/session documentation. */
export function isAppointmentTemplate(questionnaire: Questionnaire): boolean {
  return getTemplateCategory(questionnaire) === 'appointments';
}

/** True when the template is a scored measure (Measures tab), not a chart document. */
export function isMeasuresTemplate(questionnaire: Questionnaire): boolean {
  return getTemplateCategory(questionnaire) === 'measures';
}

/**
 * Runtime filter for chart document picker.
 * Excludes appointment and measure templates; requires a known non-appointment category.
 */
export function isChartDocumentTemplate(questionnaire: Questionnaire): boolean {
  const category = getTemplateCategory(questionnaire);
  if (!category) {
    // Without category metadata we cannot safely distinguish appointment templates.
    return false;
  }
  if (category === 'appointments' || category === 'measures') {
    return false;
  }
  return CHART_DOCUMENT_TEMPLATE_CATEGORIES.includes(category);
}

export interface ChartDocumentTemplateDisplay {
  id: string;
  title: string;
  categoryLabel?: string;
  description?: string;
}

export function getChartDocumentTemplateDisplay(questionnaire: Questionnaire): ChartDocumentTemplateDisplay {
  const category = getTemplateCategory(questionnaire);
  return {
    id: questionnaire.id ?? questionnaire.url ?? questionnaire.name ?? '',
    title: questionnaire.title ?? questionnaire.name ?? 'Untitled document',
    categoryLabel: category ? TEMPLATE_CATEGORY_LABELS[category] : undefined,
    description: questionnaire.description,
  };
}

/** Filter resolved templates to chart-document candidates. */
export function filterChartDocumentTemplates(templates: Questionnaire[]): Questionnaire[] {
  return templates.filter(isChartDocumentTemplate);
}

/** Filter to Treatment Planning templates for the Add Other Document modal. */
export function isTreatmentPlanningDocumentTemplate(questionnaire: Questionnaire): boolean {
  return getTemplateCategory(questionnaire) === ADD_OTHER_DOCUMENT_TEMPLATE_CATEGORY;
}

export function filterTreatmentPlanningDocumentTemplates(templates: Questionnaire[]): Questionnaire[] {
  return templates.filter(isTreatmentPlanningDocumentTemplate);
}

function templateIdentityKey(questionnaire: Questionnaire): string {
  const parts = [
    questionnaire.id,
    questionnaire.name,
    questionnaire.url?.split('/').pop(),
    questionnaire.title,
  ].filter((p): p is string => Boolean(p?.trim()));
  return parts.join(' ').toLowerCase();
}

/** Meridian IOP Continuing Recovery Plan — supplemental form, not the primary D&T template. */
export function isIopContinuingRecoveryPlanTemplate(questionnaire: Questionnaire): boolean {
  return templateIdentityKey(questionnaire).includes('continuing-recovery-plan');
}

/** Standard diagnosis + treatment plan core (`-plan-info`, `-diagnosis`, goals/objectives). */
export function isStandardDiagnosisTreatmentPlanTemplate(questionnaire: Questionnaire): boolean {
  const key = templateIdentityKey(questionnaire);
  if (key.includes('lab-request') || key.includes('lab request')) return false;
  if (key.includes('treatment-review')) return false;
  if (isIopContinuingRecoveryPlanTemplate(questionnaire)) return false;
  return key.includes('treatment-plan') || key.includes('treatment plan');
}

/** Treatment Plan templates that require supervisor cosign on the chart. */
export function isTreatmentPlanCosignTemplate(questionnaire: Questionnaire): boolean {
  return isStandardDiagnosisTreatmentPlanTemplate(questionnaire)
    || isIopContinuingRecoveryPlanTemplate(questionnaire);
}

/** Uses TreatmentPlanDocumentFormSection + AI revision (standard D&T only). */
export function usesSpecializedTreatmentPlanForm(questionnaire: Questionnaire): boolean {
  return isStandardDiagnosisTreatmentPlanTemplate(questionnaire);
}

export function episodeIsIop(
  episode: { clinicalDomain?: string; name?: string; serviceTypeCode?: string } | undefined,
): boolean {
  if (!episode) return false;
  if (episode.clinicalDomain === 'iop') return true;
  if (episode.serviceTypeCode === 'iop') return true;
  const nameLower = (episode.name ?? '').toLowerCase();
  return nameLower.includes('iop') || nameLower.includes('intensive outpatient');
}

/**
 * Primary chart treatment plan for all modalities (Individual, Group, IOP, etc.).
 * IOP episodes also require {@link resolveIopContinuingRecoveryPlanTemplate} as a second document.
 */
export function resolveDefaultTreatmentPlanTemplate(templates: Questionnaire[]): Questionnaire | undefined {
  const treatmentPlans = templates.filter(isStandardDiagnosisTreatmentPlanTemplate);
  return treatmentPlans[0];
}

/** IOP supplemental Continuing Recovery Plan template (second chart document). */
export function resolveIopContinuingRecoveryPlanTemplate(templates: Questionnaire[]): Questionnaire | undefined {
  return templates.find(isIopContinuingRecoveryPlanTemplate);
}

/** Discharge Summary templates that require supervisor cosign on the chart. */
export function isDischargeSummaryCosignTemplate(questionnaire: Questionnaire): boolean {
  const key = templateIdentityKey(questionnaire);
  return key.includes('discharge-summary') || key.includes('discharge summary');
}

/**
 * Chart documents that use supervisor cosign rules (pending co-signature badge).
 * Excludes lab requests and other treatment-planning forms that are not treatment plans.
 */
export function chartDocumentRequiresSupervisorCosign(questionnaire: Questionnaire | undefined): boolean {
  if (!questionnaire) return false;
  return isTreatmentPlanCosignTemplate(questionnaire) || isDischargeSummaryCosignTemplate(questionnaire);
}

export type QuestionnaireItemAudience = 'provider' | 'client';

export function getQuestionnaireItemAudience(item: QuestionnaireItem): QuestionnaireItemAudience | undefined {
  const ext = item.extension?.find((e) => e.url === QUESTIONNAIRE_ITEM_AUDIENCE_EXT);
  const code = ext?.valueCode;
  if (code === 'provider' || code === 'client') return code;
  return undefined;
}

/**
 * Infer who should complete a field when audience metadata is missing.
 * Signature controls default to client; section titles containing “client” hint client fields.
 */
export function inferQuestionnaireItemAudience(
  item: QuestionnaireItem,
  parentGroupTitle?: string,
): QuestionnaireItemAudience {
  const explicit = getQuestionnaireItemAudience(item);
  if (explicit) return explicit;

  const control = getItemControlCode(item);
  if (control === 'signature' || item.text?.toLowerCase().includes('signature')) {
    return 'client';
  }

  const groupHint = (parentGroupTitle ?? item.text ?? '').toLowerCase();
  if (groupHint.includes('client') || groupHint.includes('patient')) {
    return 'client';
  }

  return 'provider';
}

/** Collect linkIds the client is expected to complete on a template. */
export function collectClientEditableLinkIds(questionnaire: Questionnaire): string[] {
  const ids: string[] = [];

  const walk = (items: QuestionnaireItem[] | undefined, parentTitle?: string): void => {
    for (const item of items ?? []) {
      if (item.type === 'group') {
        walk(item.item, item.text ?? parentTitle);
        continue;
      }
      if (!item.linkId || item.type === 'display') continue;
      if (inferQuestionnaireItemAudience(item, parentTitle) === 'client') {
        ids.push(item.linkId);
      }
    }
  };

  walk(questionnaire.item);
  return ids;
}
